import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	DeleteIcon,
	FlexGap,
	Heading,
	IconButton,
	Popover,
	PropertyChip,
	Tabs,
	TargetIcon,
	tokens,
} from '@treams/treams-ui';

import { OrganizationGoalsList } from '../OrganizationGoals';
import { StatefulCollectiveGoalsV2 } from '../collective-goals/CollectiveGoalsV2';
import { GoalSelectorVisibility } from './GoalSelectorVisibility';

const Container = styled(Box)`
	background: ${tokens.surface.secondary};
	overflow-y: auto;
`;

const Footer = styled(Flex)`
	background: ${tokens.surface.white};
	border-top: 1px solid ${tokens.border.subtle};
`;

function GoalSelectorModal(props: {
	goalTargetIsUser?: boolean;
	onDismiss: () => void;
	onSelect: (goal: graphql.Goal, shareGoal: boolean) => void;
}) {
	const { t } = useTranslation();

	const [goal, setGoal] = useState<graphql.Goal | undefined>(undefined);
	const [step, setStep] = useState<'GOAL' | 'VISIBILITY'>('GOAL');
	const AddGoalsToOrganisationGoals = useFeature(graphql.FeatureName.AddGoalsToOrganisationGoals);

	const [tab, setTab] = useState(0);

	if (step === 'VISIBILITY') {
		return (
			<GoalSelectorVisibility
				onSelect={(shareGoal) => props.onSelect(goal!, shareGoal)}
				onBack={() => setStep('GOAL')}
			/>
		);
	}

	return (
		<Flex flexDirection="column" flexGrow={1} width={tab === 0 ? '100vw' : 'auto'}>
			<Box flexGrow={0} flexShrink={0}>
				<Box p={3} pb={0}>
					<Heading>{t('goals.selector.header.title')}</Heading>
				</Box>

				<Tabs>
					<Tabs.Item
						selected={tab === 0}
						title={t('goals.tab.collective.title')}
						onClick={() => setTab(0)}
					/>
					{!AddGoalsToOrganisationGoals && (
						<Tabs.Item
							selected={tab === 1}
							title={t('goals.tab.organization.title')}
							onClick={() => setTab(1)}
						/>
					)}
				</Tabs>
			</Box>
			<Container flexGrow={1} flexShrink={1}>
				{tab === 0 && (
					<StatefulCollectiveGoalsV2 hideArchived selectedGoal={goal} onSelect={setGoal} />
				)}
				{tab === 1 && !AddGoalsToOrganisationGoals && (
					<Box p={3}>
						<OrganizationGoalsList hideArchived selectedGoal={goal} onSelect={setGoal} />
					</Box>
				)}
			</Container>
			<Footer
				p={3}
				width="100%"
				flexGrow={0}
				flexShrink={0}
				alignItems="center"
				justifyContent="flex-end"
			>
				<Button
					type="secondary"
					title={t('goals.new.template.footer.cancel')}
					flexGrow={0}
					flexShrink={0}
					onClick={() => props.onDismiss()}
				/>
				<Button
					type="primary"
					title={t('goals.new.template.footer.continue')}
					flexGrow={0}
					flexShrink={0}
					ml={2}
					onClick={() => {
						if (!props.goalTargetIsUser) return props.onSelect(goal!, true);

						setStep('VISIBILITY');
					}}
					disabled={!goal}
				/>
			</Footer>
		</Flex>
	);
}

export default function GoalSelectorV2(props: {
	personalGoals?: boolean;
	goalTargetIsUser?: boolean;
	goal?: graphql.Goal;
	onChange: (goal?: graphql.Goal, visibility?: graphql.GoalVisibility) => void;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);

	return (
		<FlexGap alignItems="center" gap={tokens.gap.element}>
			<Popover.Target
				contents={
					<GoalSelectorModal
						goalTargetIsUser={props.goalTargetIsUser}
						onDismiss={() => setVisible(false)}
						onSelect={(goal, shareGoal) => {
							const visibility = shareGoal
								? graphql.GoalVisibility.Public
								: graphql.GoalVisibility.Private;
							props.onChange(goal, visibility);
							setVisible(false);
						}}
					/>
				}
				visible={visible}
				center
				onDismiss={() => setVisible(false)}
			>
				<PropertyChip
					icon={<TargetIcon />}
					label={props.goal ? localize(props.goal.title).text : t('goals.selector.select')}
					onClick={() => setVisible(true)}
				/>
			</Popover.Target>
			{props.goal && (
				<IconButton
					type="destructive"
					icon={<DeleteIcon />}
					onClick={() => props.onChange(undefined)}
				/>
			)}
		</FlexGap>
	);
}
